import { Sparkles, Droplet } from "lucide-react";

const DRY_TOPPINGS = [
  { name: "Rainbow Sprinkles", emoji: "🌈" },
  { name: "Choc Chips", emoji: "🍫" },
  { name: "Mini Marshmallows", emoji: "☁️" },
];

const SAUCES = [
  { name: "Chocolate", swatch: "#3B1A0B" },
  { name: "Strawberry", swatch: "#E63268" },
  { name: "Salted Caramel", swatch: "#B8752E" },
];

export default function Menu() {
  return (
    <section id="menu" className="relative py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Header */}
        <div className="flex items-end justify-between gap-8 mb-16 flex-col md:flex-row">
          <div>
            <div className="font-mono-accent text-[11px] text-[#5AB0FF] mb-4">
              The Menu
            </div>
            <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] max-w-3xl">
              Fresh vanilla.{" "}
              <span className="italic text-white/70">Your rules.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/60 md:text-right leading-relaxed">
            Every cup starts with our creamy vanilla soft-serve. You pick one
            dry topping and one sauce. Simple. Endless combos.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Base: Vanilla */}
          <div
            data-testid="menu-base"
            className="lg:col-span-4 relative rounded-3xl border border-white/10 bg-[#15161C] p-8 md:p-10 overflow-hidden group transition-colors hover:border-white/25"
          >
            <div className="font-mono-accent text-[11px] text-white/50 mb-6">
              STEP 01 · THE BASE
            </div>
            <div className="mb-8 flex items-center justify-center h-40">
              <div className="relative">
                <div className="absolute inset-0 bg-[#8B5CF6] blur-3xl opacity-30 rounded-full" />
                <div className="relative font-display text-[120px] leading-none">
                  🍨
                </div>
              </div>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Fresh Vanilla
            </h3>
            <p className="mt-3 text-white/60 leading-relaxed">
              Real dairy soft-serve. Made with real milk. Served in a cup.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1 font-mono-accent text-[10px] text-white/60">
              <span className="h-1.5 w-1.5 rounded-full bg-[#5AB0FF]" /> Included
            </div>
          </div>

          {/* Dry Toppings */}
          <div
            data-testid="menu-toppings"
            className="lg:col-span-4 relative rounded-3xl border border-white/10 bg-[#15161C] p-8 md:p-10 overflow-hidden group transition-colors hover:border-[#8B5CF6]/40"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="font-mono-accent text-[11px] text-[#8B5CF6]">
                STEP 02 · PICK 1 TOPPING
              </div>
              <Sparkles size={18} className="text-[#8B5CF6]" />
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-8">
              Dry Toppings
            </h3>
            <ul className="space-y-3">
              {DRY_TOPPINGS.map((t, i) => (
                <li
                  key={t.name}
                  data-testid={`topping-${i}`}
                  className="group/item flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/30 px-4 py-4 transition-colors hover:border-[#8B5CF6]/60 hover:bg-[#8B5CF6]/5"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl leading-none">{t.emoji}</span>
                    <span className="font-body text-base font-medium text-white">
                      {t.name}
                    </span>
                  </div>
                  <span className="font-mono-accent text-[10px] text-white/40">
                    0{i + 1}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Sauces */}
          <div
            data-testid="menu-sauces"
            className="lg:col-span-4 relative rounded-3xl border border-white/10 bg-[#15161C] p-8 md:p-10 overflow-hidden group transition-colors hover:border-[#5AB0FF]/40"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="font-mono-accent text-[11px] text-[#5AB0FF]">
                STEP 03 · PICK 1 SAUCE
              </div>
              <Droplet size={18} className="text-[#5AB0FF]" />
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-8">
              Sauces
            </h3>
            <ul className="space-y-3">
              {SAUCES.map((s, i) => (
                <li
                  key={s.name}
                  data-testid={`sauce-${i}`}
                  className="group/item flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/30 px-4 py-4 transition-colors hover:border-[#5AB0FF]/60 hover:bg-[#5AB0FF]/5"
                >
                  <div className="flex items-center gap-4">
                    <span
                      className="h-6 w-6 rounded-full border border-white/20"
                      style={{ background: s.swatch }}
                      aria-hidden="true"
                    />
                    <span className="font-body text-base font-medium text-white">
                      {s.name}
                    </span>
                  </div>
                  <span className="font-mono-accent text-[10px] text-white/40">
                    0{i + 1}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Combos strip */}
        <div className="mt-10 rounded-3xl border border-white/10 bg-gradient-to-r from-[#15161C] via-[#181228] to-[#15161C] p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-mono-accent text-[11px] text-white/60">
            3 toppings × 3 sauces
          </div>
          <div className="font-display text-3xl md:text-5xl font-bold tracking-tighter text-center">
            <span className="text-[#8B5CF6]">9 signature</span> combinations
          </div>
          <div className="font-mono-accent text-[11px] text-white/60">
            Served under 15 seconds
          </div>
        </div>
      </div>
    </section>
  );
}
